import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { notificationsApi } from "@/api/ux";
import {
  pausePollingOnSessionExpiry,
  retryUnlessSessionExpired,
} from "@/api/polling";
import { CountBadge } from "@/components/ui/CountBadge";
import { cn } from "@/design-system/lib/utils";
import type { NotificationDto } from "@/types/api";

/**
 * Header bell (Deal Pipeline spec §8.2).
 *
 * Polls the cross-module notifications feed every 60 seconds and opens
 * a popover with the most recent entries on click. Replaces the prior
 * stopgap that linked to /messages and mirrored the Messages unread
 * count — the bell now surfaces the real notification feed.
 *
 * Click on a notification entry both routes to its LinkUrl AND marks it
 * read in a single action. "View all" routes to the full-page feed.
 */
const POLL_INTERVAL_MS = 60_000;

export function NotificationBell({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ["notifications", "feed", { unreadOnly: false, page: 1, pageSize: 20 }],
    queryFn: () => notificationsApi.list(false, 1, 20),
    // Stop polling once the session is gone — a 401 means every further tick
    // is a guaranteed failure (the 2026-06-11 perf logs' "401 polling").
    refetchInterval: pausePollingOnSessionExpiry(POLL_INTERVAL_MS),
    retry: retryUnlessSessionExpired,
    // The interval poll is the freshness mechanism — no extra refetch on
    // every tab focus (perf PR; the global default is also focus-off).
    // staleTime stays short so a remount between ticks shows fresh data.
    staleTime: 30_000,
  });

  const unreadCount = list.data?.unreadCount ?? 0;
  const notifications = list.data?.data ?? [];

  // Close the popover on outside-click / Escape (matches the avatar
  // menu pattern used in TopBar).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const tooltip = unreadCount > 0
    ? `Notifications — ${unreadCount} unread`
    : "Notifications";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-label={tooltip}
        title={tooltip}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative inline-flex items-center justify-center h-10 w-10 rounded-md",
          "text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5",
          "focus-visible:outline-none focus-visible:shadow-goldRing",
          className,
        )}
      >
        <Bell size={18} strokeWidth={1.5} aria-hidden />
        <CountBadge
          count={unreadCount}
          tone="error"
          size="sm"
          className="absolute top-[6px] right-[6px]"
        />
      </button>

      {open && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          loading={list.isLoading}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

interface PanelProps {
  notifications: NotificationDto[];
  unreadCount: number;
  loading: boolean;
  onClose: () => void;
}

function NotificationPanel({ notifications, unreadCount, loading, onClose }: PanelProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", "feed"] }),
  });

  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", "feed"] }),
  });

  const onEntryClick = (n: NotificationDto) => {
    if (!n.isRead) markRead.mutate(n.id);
    if (n.linkUrl) {
      navigate(n.linkUrl);
      onClose();
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      className={cn(
        "absolute right-0 mt-space-2 w-[360px] max-w-[90vw] rounded-md shadow-lg z-50",
        "border border-border-light bg-white-pure text-onyx",
      )}
    >
      <div className="flex items-center justify-between px-space-3 py-space-3 border-b border-border-light">
        <div className="text-small font-semibold">Notifications</div>
        <button
          type="button"
          className="text-label uppercase tracking-label text-gray-mid hover:text-onyx disabled:opacity-50"
          disabled={unreadCount === 0 || markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          {markAll.isPending ? "Marking…" : "Mark all read"}
        </button>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {loading && (
          <div className="px-space-3 py-space-4 text-label uppercase tracking-label text-gray-mid">
            Loading…
          </div>
        )}
        {!loading && notifications.length === 0 && (
          <div className="px-space-3 py-space-4 text-small text-gray-mid">
            You're all caught up.
          </div>
        )}
        {notifications.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onEntryClick(n)}
            className={cn(
              "w-full text-left px-space-3 py-space-3 border-b border-border-light last:border-b-0",
              "hover:bg-white-warm focus:outline-none focus-visible:bg-white-warm",
              !n.isRead && "bg-gold/[0.04]",
            )}
          >
            <div className="flex items-start gap-space-2">
              <span
                aria-hidden
                className={cn(
                  "mt-[6px] h-2 w-2 shrink-0 rounded-full",
                  n.category === "Urgent" ? "bg-status-error" : "bg-gold",
                  n.isRead && "opacity-30",
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="text-small font-semibold truncate">{n.title}</div>
                {n.body && (
                  <div className="text-label text-gray-mid line-clamp-2 mt-[2px]">{n.body}</div>
                )}
                <div className="text-label text-gray-mid mt-[2px]">
                  {formatAgo(n.createdAt)}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="px-space-3 py-space-2 border-t border-border-light text-center">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-label uppercase tracking-label text-gold hover:text-gold-shadow"
        >
          View all
        </Link>
      </div>
    </div>
  );
}

function formatAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
